/**
 * ADR-70 cut 6a (hitlist sections 15 and 16): what a ring did, lot by lot,
 * for the people running it. Each lot's bids carry their markers (a bid
 * the engine placed on a standing max, one the operator took in confirm
 * mode, an equal-bid award, a bid taken back), and each lot's time in the
 * ring is the sample the pace figures are made from.
 *
 * A pure fold over the ring's own events. The clerk console folds the floor
 * wire as it arrives; the API folds the durable log when a console opens or
 * has missed events. Both read the same fields, so a reload shows the rows
 * the screen showed before it. `maxAmount` is never read: the floor wire
 * does not carry it (redact.ts), and a history that needed it could not be
 * rebuilt from that wire.
 *
 * Nothing here is authority. A bid's row is a description of events the
 * ring already decided; no command reads this.
 */

import type { BidOrigin, BidSource, DeviceClass, DomainEvent, LotOutcome } from './types.js';

/** ADR-70 cut 6b: where a bid came from, as the record says. */
export interface BidVia {
  readonly origin: BidOrigin | null;
  readonly deviceClass: DeviceClass | null;
}

/** What took a bid back. CORRECTED: the clerk's floor-bid correction.
 *  RESTORED_ONLINE: an equal-bid award undone, the online bid holding again.
 *  RETRACTED (cut 6c): the clerk took back the last bid, any source.
 *  RESET (cut 6c): the lot's bidding was reset; every standing bid goes. */
export type BidUndoKind = 'CORRECTED' | 'RESTORED_ONLINE' | 'RETRACTED' | 'RESET';

export interface BidRow {
  readonly seq: number;
  /** The engine's commit time (serverTs). */
  readonly at: number;
  readonly amount: number;
  /** The ring's enum, never a label: FLOOR, ONLINE, or PROXY (placed by the
   *  engine on a standing max bid). */
  readonly source: BidSource;
  readonly bidderId: string;
  readonly bidderRef: string;
  /** The paddle the clerk typed for a floor bid; null online. */
  readonly paddle: string | null;
  /** The role that entered it, as the record says (cut 4); null on a wire
   *  or a log written before cut 4. */
  readonly actorRole: string | null;
  /** ADR-70 cut 6b (hitlist 8.2, 16): the surface and the device class the
   *  bid came from. A held bid the clerk took has its own (from its
   *  ONLINE_BID_PENDING), not the clerk's. Null where the record does not
   *  say: before 6b, a client that did not say, a bid an equal-bid award
   *  gave back to online. Absent on a row from a 6a API. */
  readonly origin?: BidOrigin | null;
  readonly deviceClass?: DeviceClass | null;
  /** The operator took it in confirm mode (a held internet bid accepted). */
  operatorAccepted: boolean;
  /** An equal-bid decision gave this bid the board: to which side, whether
   *  the clerk or the ring's policy decided, and the override's seq. */
  equalBidAward: { readonly to: 'ONSITE' | 'ONLINE'; readonly cause: 'OPERATOR' | 'POLICY'; readonly overrideSeq: number } | null;
  /** A later fact took it back. The row stays: the log never changes. */
  undone: { readonly bySeq: number; readonly kind: BidUndoKind; readonly reason: string } | null;
}

/** How a lot left the ring (a void is a later fact, kept as `voided`). */
export type DepartureOutcome = Exclude<LotOutcome, 'VOIDED'>;

export interface LotActivity {
  readonly lotId: string;
  /** The last time the lot was put in the ring (SET_CURRENT_LOT). */
  setAt: number | null;
  /** When this offering began: put in the ring, or opened without that. */
  enteredAt: number | null;
  openedAt: number | null;
  /** When it left the ring sold, unsold or passed; null while it is there. */
  closedAt: number | null;
  outcome: DepartureOutcome | null;
  /** The hammer (the unit price for a group); null unless SOLD. */
  price: number | null;
  /** The sale it made was voided or turned back afterwards (ADR-65). */
  voided: boolean;
  /** The group lot this one sold with, when it was not the lead. */
  soldWithLotId: string | null;
  /** Accepted bids this offering, less the ones taken back. */
  bidCount: number;
  /** This offering's bids, oldest first. null: not carried (the API sends
   *  bids only for the lots asked for; the count is still right). */
  bids: BidRow[] | null;
}

/** One lot (or group) leaving the ring: how long it was there, and how
 *  many catalog lots left with it. */
export interface PaceSample {
  readonly lotId: string;
  readonly at: number;
  readonly ms: number;
  readonly lots: number;
}

export interface RingActivity {
  lots: Record<string, LotActivity>;
  currentLotId: string | null;
  /** Every departure from the ring, in order. */
  samples: PaceSample[];
  /** The highest seq folded. Events at or below it are ignored, so the
   *  same event folded twice (a seed overlapping the live wire) is harmless. */
  lastSeq: number;
  /** The seq of a bid the operator just took in confirm mode: the
   *  resolution is committed one seq before the bid (reduce.ts). */
  expectAccepted: number | null;
  /** The bid an equal-bid award is about to place (its winnerBidSeq). */
  expectAward: { readonly seq: number; readonly to: 'ONSITE' | 'ONLINE'; readonly cause: 'OPERATOR' | 'POLICY'; readonly overrideSeq: number } | null;
  /** ADR-70 cut 6b: held internet bids by pending id, with where they came
   *  from, until the clerk resolves them. */
  held?: Record<string, BidVia>;
  /** Where the bid at `expectAccepted` came from. */
  acceptedVia?: BidVia | null;
}

export function emptyRingActivity(): RingActivity {
  return { lots: {}, currentLotId: null, samples: [], lastSeq: 0, expectAccepted: null, expectAward: null, held: {}, acceptedVia: null };
}

function viaOf(ev: { readonly origin?: BidOrigin; readonly deviceClass?: DeviceClass }): BidVia {
  return { origin: ev.origin ?? null, deviceClass: ev.deviceClass ?? null };
}

function lotOf(a: RingActivity, lotId: string): LotActivity {
  let l = a.lots[lotId];
  if (!l) {
    l = { lotId, setAt: null, enteredAt: null, openedAt: null, closedAt: null, outcome: null, price: null, voided: false, soldWithLotId: null, bidCount: 0, bids: [] };
    a.lots[lotId] = l;
  }
  return l;
}

/** A lot that already left the ring and is opened again starts a new
 *  offering: its old time stays in the samples, its old bids leave the
 *  screen (they stay in the log). */
function freshOffering(l: LotActivity, at: number): void {
  l.enteredAt = l.setAt ?? at;
  l.openedAt = null;
  l.closedAt = null;
  l.outcome = null;
  l.price = null;
  l.voided = false;
  l.soldWithLotId = null;
  l.bidCount = 0;
  l.bids = [];
}

function depart(a: RingActivity, l: LotActivity, outcome: DepartureOutcome, at: number, lots: number): void {
  l.outcome = outcome;
  l.closedAt = at;
  const from = l.enteredAt ?? l.openedAt;
  if (from !== null) a.samples.push({ lotId: l.lotId, at, ms: Math.max(0, at - from), lots });
}

function findBid(l: LotActivity | undefined, pred: (b: BidRow) => boolean): BidRow | undefined {
  if (!l?.bids) return undefined;
  for (let i = l.bids.length - 1; i >= 0; i--) if (pred(l.bids[i]!)) return l.bids[i];
  return undefined;
}

/**
 * Fold one event into `a`, in place, and return it. Events must arrive in
 * seq order (the wire and the log both guarantee it); one at or below
 * `lastSeq` is ignored.
 */
export function foldRingActivity(a: RingActivity, ev: DomainEvent): RingActivity {
  if (ev.seq <= a.lastSeq) return a;
  a.lastSeq = ev.seq;
  const at = ev.serverTs;
  switch (ev.type) {
    case 'CURRENT_LOT_SET': {
      a.currentLotId = ev.lotId;
      const l = lotOf(a, ev.lotId);
      l.setAt = at;
      // Going back to a lot that already left (to read a result, to void a
      // sale) is not a new offering; opening it again is.
      if (l.outcome === null) l.enteredAt = at;
      break;
    }
    case 'LOT_OPENED': {
      a.currentLotId = ev.lotId;
      const l = lotOf(a, ev.lotId);
      if (l.outcome !== null) freshOffering(l, at);
      if (l.enteredAt === null) l.enteredAt = at;
      l.openedAt = at;
      break;
    }
    case 'BID_ACCEPTED': {
      const l = lotOf(a, ev.lotId);
      const award = a.expectAward?.seq === ev.seq ? a.expectAward : null;
      const taken = a.expectAccepted === ev.seq;
      // A held bid the clerk took names the bidder under the clerk's command,
      // so its event has no origin of its own: it is the held bid's.
      const via = ev.origin !== undefined || ev.deviceClass !== undefined ? viaOf(ev) : taken && a.acceptedVia ? a.acceptedVia : viaOf(ev);
      const row: BidRow = {
        seq: ev.seq,
        at,
        amount: ev.bidAmount,
        source: ev.bidSource,
        bidderId: ev.bidderId,
        bidderRef: ev.bidderRef,
        paddle: ev.paddleNumber,
        actorRole: ev.actorRole ?? null,
        origin: via.origin,
        deviceClass: via.deviceClass,
        operatorAccepted: taken,
        equalBidAward: award ? { to: award.to, cause: award.cause, overrideSeq: award.overrideSeq } : null,
        undone: null,
      };
      a.expectAccepted = null;
      a.acceptedVia = null;
      a.expectAward = null;
      l.bidCount++;
      l.bids?.push(row);
      break;
    }
    case 'ONLINE_BID_PENDING':
      if (ev.origin !== undefined || ev.deviceClass !== undefined) (a.held ??= {})[ev.pendingBidId] = viaOf(ev);
      break;
    case 'PENDING_BID_RESOLVED': {
      const via = a.held?.[ev.pendingBidId] ?? null;
      if (a.held) delete a.held[ev.pendingBidId];
      if (ev.result === 'ACCEPTED') {
        a.expectAccepted = ev.seq + 1;
        a.acceptedVia = via;
      }
      break;
    }
    case 'EQUAL_BID_OVERRIDDEN': {
      if (ev.restoresSeq === null) {
        a.expectAward = { seq: ev.winnerBidSeq, to: ev.awardedTo, cause: ev.cause, overrideSeq: ev.seq };
        break;
      }
      // A restore: the award it reverses placed a bid; that bid is undone
      // and the earlier online bid holds again, with no new bid.
      const l = a.lots[ev.lotId];
      const b = findBid(l, (r) => r.equalBidAward?.overrideSeq === ev.restoresSeq && r.undone === null);
      if (l && b) {
        b.undone = { bySeq: ev.seq, kind: 'RESTORED_ONLINE', reason: ev.reason };
        l.bidCount = Math.max(0, l.bidCount - 1);
      }
      break;
    }
    case 'FLOOR_BID_CORRECTED': {
      const l = a.lots[ev.lotId];
      const b = findBid(l, (r) => r.seq === ev.reversesSeq && r.undone === null);
      // Not carried (bids === null): the count still drops, the row is not here.
      if (l && (b || l.bids === null)) l.bidCount = Math.max(0, l.bidCount - 1);
      if (b) b.undone = { bySeq: ev.seq, kind: 'CORRECTED', reason: ev.reason };
      break;
    }
    case 'BID_RETRACTED': {
      const l = a.lots[ev.lotId];
      const b = findBid(l, (r) => r.seq === ev.reversesSeq && r.undone === null);
      if (l && (b || l.bids === null)) l.bidCount = Math.max(0, l.bidCount - 1);
      if (b) b.undone = { bySeq: ev.seq, kind: 'RETRACTED', reason: ev.reason ?? '' };
      break;
    }
    case 'BIDDING_RESET': {
      const l = a.lots[ev.lotId];
      if (!l) break;
      for (const b of l.bids ?? []) if (b.undone === null) b.undone = { bySeq: ev.seq, kind: 'RESET', reason: ev.reason ?? '' };
      l.bidCount = 0;
      break;
    }
    case 'LOT_REOPENED': {
      // Cut 6c: a lot reopened after it passed or no-saled has not left after
      // all. Its departure is taken back (the pace sample with it); its time
      // in the ring runs on from when it came in, and its bids stand.
      const l = a.lots[ev.lotId];
      if (!l || l.outcome === null || l.outcome === 'SOLD') break;
      for (let i = a.samples.length - 1; i >= 0; i--) {
        if (a.samples[i]!.lotId === l.lotId) { a.samples.splice(i, 1); break; }
      }
      l.outcome = null;
      l.closedAt = null;
      a.currentLotId = ev.lotId;
      break;
    }
    case 'LOT_REMOVED_FROM_RING': {
      // Cut 6d: the lot left the ring with no result and stays in the sale.
      // No outcome and no pace sample (it was not sold, passed or no-saled);
      // when it comes back it is a new offering, so this one's time and bids
      // leave the screen (they stay in the log).
      if (a.currentLotId === ev.lotId) a.currentLotId = null;
      for (const id of ev.group?.lotIds ?? [ev.lotId]) {
        const l = a.lots[id];
        if (!l || l.outcome !== null) continue;
        l.enteredAt = null;
        l.openedAt = null;
        l.bidCount = 0;
        if (l.bids !== null) l.bids = [];
      }
      break;
    }
    case 'LOT_SOLD': {
      const l = lotOf(a, ev.lotId);
      const others = ev.group ? ev.group.lotIds.filter((id) => id !== ev.lotId) : [];
      l.price = ev.price;
      depart(a, l, 'SOLD', at, 1 + others.length);
      for (const id of others) {
        const g = lotOf(a, id);
        g.outcome = 'SOLD';
        g.closedAt = at;
        g.soldWithLotId = ev.lotId;
        g.price = null;
      }
      break;
    }
    case 'LOT_NO_SALE':
      depart(a, lotOf(a, ev.lotId), 'NO_SALE', at, 1);
      break;
    case 'LOT_PASSED':
      depart(a, lotOf(a, ev.lotId), 'PASSED', at, 1);
      break;
    case 'SALE_VOIDED':
      for (const id of ev.group?.lotIds ?? [ev.lotId]) {
        const l = a.lots[id];
        if (l) l.voided = true;
      }
      break;
    default:
      break;
  }
  return a;
}

export function ringActivityFromEvents(events: readonly DomainEvent[]): RingActivity {
  const a = emptyRingActivity();
  for (const ev of events) foldRingActivity(a, ev);
  return a;
}

/** The most recent lot to leave the ring, or null. */
export function previousLotId(a: RingActivity): string | null {
  for (let i = a.samples.length - 1; i >= 0; i--) {
    const id = a.samples[i]!.lotId;
    if (id !== a.currentLotId) return id;
  }
  return null;
}

/**
 * A copy for the wire: every lot's timings and counts, and bids only for
 * the lots named (null for the rest). The current lot and the one before
 * it are always carried, which is what a console opening mid-sale shows.
 */
export function ringActivityForWire(a: RingActivity, alsoLotIds: readonly string[] = []): RingActivity {
  const keep = new Set<string>(alsoLotIds);
  if (a.currentLotId) keep.add(a.currentLotId);
  const prev = previousLotId(a);
  if (prev) keep.add(prev);
  const lots: Record<string, LotActivity> = {};
  for (const [id, l] of Object.entries(a.lots)) {
    lots[id] = { ...l, bids: keep.has(id) && l.bids ? l.bids.map((b) => ({ ...b })) : null };
  }
  return { ...a, lots, samples: [...a.samples], held: { ...(a.held ?? {}) } };
}

// ---------------------------------------------------------------------------
// Pace (hitlist section 15)
// ---------------------------------------------------------------------------

export interface RingPace {
  /** The lot in the ring now, how long it has been there; null when the
   *  ring holds no lot, or holds one that already left (a revisit). */
  readonly timeInRingMs: number | null;
  readonly avgPerLotMs: number | null;
  readonly lotsPerHour: number | null;
  /** Over the last ten departures. */
  readonly last10LotsPerHour: number | null;
  readonly last10PerLotMs: number | null;
  readonly completed: number;
  readonly total: number;
  readonly remaining: number;
  /** now + remaining at the last-10 pace; null with nothing to go on, or
   *  nothing left. */
  readonly estimatedEndAt: number | null;
}

const HOUR_MS = 3_600_000;
export const PACE_RECENT_DEPARTURES = 10;

function perLot(samples: readonly PaceSample[]): number | null {
  let ms = 0;
  let lots = 0;
  for (const s of samples) { ms += s.ms; lots += s.lots; }
  return lots > 0 ? ms / lots : null;
}

/**
 * The ring's pace. `lotIds` is the run this ring sells (its catalog lots,
 * scratched ones left out): a lot counts as complete when it left the ring
 * and the sale it made still stands.
 */
export function ringPace(a: RingActivity, opts: { readonly now: number; readonly lotIds: readonly string[] }): RingPace {
  const avg = perLot(a.samples);
  const recent = perLot(a.samples.slice(-PACE_RECENT_DEPARTURES));
  const total = opts.lotIds.length;
  let completed = 0;
  for (const id of opts.lotIds) {
    const l = a.lots[id];
    if (l && l.outcome !== null && !l.voided) completed++;
  }
  const remaining = Math.max(0, total - completed);
  const cur = a.currentLotId ? a.lots[a.currentLotId] : undefined;
  const timeInRingMs = cur && cur.outcome === null && cur.enteredAt !== null ? Math.max(0, opts.now - cur.enteredAt) : null;
  const pace = recent ?? avg;
  // The lot in the ring is part done: credit what it has spent, up to a
  // whole lot's worth.
  const credit = pace !== null && timeInRingMs !== null ? Math.min(timeInRingMs, pace) : 0;
  return {
    timeInRingMs,
    avgPerLotMs: avg,
    lotsPerHour: avg ? HOUR_MS / avg : null,
    last10LotsPerHour: recent ? HOUR_MS / recent : null,
    last10PerLotMs: recent,
    completed,
    total,
    remaining,
    estimatedEndAt: pace !== null && remaining > 0 ? opts.now + remaining * pace - credit : null,
  };
}
